import { isBaseFont } from '@/utils/fontAssetUtils';
import type { Node } from '@/types/node';
import type { CompositorNodeV3 } from '@/types/nodes/compositor';
import type { Layer } from 'web';

export type MissingCustomFontFace = {
  faceKey: string;
  fontFamilyKey: string;
  weight: number;
  isItalic: boolean;
};

export type MissingCustomFontFamily = {
  fontFamilyKey: string;
  faces: MissingCustomFontFace[];
};

export type FontStyleCombination = { weight: number; isItalic: boolean };

/** Canonical key for a workspace font face (family + weight + italic), matching recipe font API rows. */
export function makeWorkspaceFontFaceKey(fontFamilyKey: string, weight: number, isItalic: boolean): string {
  return JSON.stringify([fontFamilyKey, Number(weight), Boolean(isItalic)]);
}

export function encodeFontStyleValue(weight: number, isItalic: boolean): string {
  return `${weight}\u001f${isItalic ? '1' : '0'}`;
}

export function decodeFontStyleValue(value: string): FontStyleCombination | null {
  const parts = value.split('\u001f');
  if (parts.length !== 2) {
    return null;
  }
  const weight = Number(parts[0]);
  const isItalic = parts[1] === '1';
  if (!Number.isFinite(weight)) {
    return null;
  }
  return { weight, isItalic };
}

function tryCollectMissingCustomFontFace(
  layer: Layer,
  allowedWorkspaceCustomFontFaceKeys: ReadonlySet<string>,
): MissingCustomFontFace | null {
  if (layer.kind.type !== 'text') {
    return null;
  }
  const font = layer.kind.fontOptions.font;
  const { weight, italic: isItalic } = layer.kind.fontOptions;
  const fontFamilyKey = font.type === 'custom' ? font.data.resourceKey : font.data;
  const faceKey = makeWorkspaceFontFaceKey(fontFamilyKey, weight, isItalic);
  const isBaseFontFamilyKey = isBaseFont(fontFamilyKey);
  if (isBaseFontFamilyKey || allowedWorkspaceCustomFontFaceKeys.has(faceKey)) {
    return null;
  }

  return { faceKey, fontFamilyKey, weight, isItalic };
}

/**
 * Groups missing custom font faces (not present in the user's allowed workspace font list) by `resourceKey`.
 * Built-in fonts are ignored — only custom uploads can be workspace-backed.
 */
export function collectMissingWorkspaceFontFamiliesFromNodes(
  nodes: Node[],
  allowedWorkspaceCustomFontFaceKeys: ReadonlySet<string>,
): MissingCustomFontFamily[] {
  const faceByKey = new Map<string, MissingCustomFontFace>();
  for (const node of nodes) {
    if (node.type !== 'compv3') {
      continue;
    }
    const compositor = node.data as CompositorNodeV3;
    const layers = compositor?.data?.layers;
    if (!layers || typeof layers !== 'object') {
      continue;
    }
    for (const layer of Object.values(layers)) {
      const face = tryCollectMissingCustomFontFace(layer, allowedWorkspaceCustomFontFaceKeys);
      if (!face) {
        continue;
      }
      faceByKey.set(face.faceKey, face);
    }
  }
  const byFamily = new Map<string, MissingCustomFontFace[]>();
  for (const face of faceByKey.values()) {
    const list = byFamily.get(face.fontFamilyKey) ?? [];
    list.push(face);
    byFamily.set(face.fontFamilyKey, list);
  }
  return [...byFamily.entries()]
    .map(([fontFamilyKey, faces]) => ({
      fontFamilyKey,
      faces: faces.sort((a, b) => a.faceKey.localeCompare(b.faceKey)),
    }))
    .sort((a, b) => a.fontFamilyKey.localeCompare(b.fontFamilyKey));
}
